// src/components/Feedback.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/i18n';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Alert,
  Avatar,
  Stack,
  ListItemText,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import InboxIcon from '@mui/icons-material/Inbox';
import OutboxIcon from '@mui/icons-material/Outbox';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import API_ENDPOINTS from '../config/apiConfig';
import FEEDBACK_BADGES from '../config/feedbackBadges';
import FixedSnackbar from './FixedSnackbar';
import LoadingButton from './LoadingButton';

// ── helper: render badge icon (image path vs emoji/text) ──────────────────────
function BadgeIcon({ icon, size = 26 }) {
  if (!icon) return null;
  if (typeof icon === 'string' && icon.startsWith('/')) {
    return (
      <img
        src={icon}
        alt="badge"
        style={{ width: size, height: size, objectFit: 'contain', display: 'block', background: 'none' }}
      />
    );
  }
  return <span style={{ fontSize: size * 1, lineHeight: 1 }}>{icon}</span>;
}

// ── helper: format date ───────────────────────────────────────────────────────
const LANG_TO_LOCALE = { spanish: 'es', english: 'en' };

function formatDate(dateStr, lang) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const locale = LANG_TO_LOCALE[lang] || lang || 'en';
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── badge lookup map ──────────────────────────────────────────────────────────
const BADGE_MAP = Object.fromEntries(FEEDBACK_BADGES.map(b => [b.id, b]));

// ── sub-component: FeedbackCard ───────────────────────────────────────────────
function FeedbackCard({ entry, direction }) {
  const { t, lang } = useTranslation();
  const badge = entry.badge_id ? BADGE_MAP[entry.badge_id] : null;
  const isDeleted = direction === 'received' ? entry.from_deleted : entry.to_deleted;
  const name = direction === 'received'
    ? (entry.from_name || entry.from_email || '')
    : (entry.to_name || entry.to_email || '');
  const displayName = isDeleted ? `${name} (${t('feedback_deleted_user')})` : name;
  const personLabel = direction === 'received'
    ? `${t('feedback_from')}: ${displayName}`
    : `${t('feedback_to')}: ${displayName}`;

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#62B6CB' }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1B4965' }}>
            {personLabel}
          </Typography>
          {badge && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                ml: 'auto !important',
                bgcolor: '#e8f4f8',
                color: '#1B4965',
                fontWeight: 500,
                borderRadius: 2,
                px: 1,
                py: 0.5,
              }}
            >
              <BadgeIcon icon={badge.icon} size={48} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1B4965' }}>
                {t(`feedback_badge_${badge.id}_name`)}
              </Typography>
            </Box>
          )}
        </Stack>
        <Typography variant="body2" sx={{ mt: 1, mb: 1, color: '#333', whiteSpace: 'pre-wrap' }}>
          {entry.message}
        </Typography>
        <Typography variant="caption" sx={{ color: '#888' }}>
          {formatDate(entry.submitted_at?.$date || entry.submitted_at, lang)}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── main Feedback component ───────────────────────────────────────────────────
export default function Feedback() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchParams, setSearchParams] = useSearchParams();
  const subTabKeys = ['give', 'received', 'given'];
  const subTabKey = searchParams.get('sub') || 'give';
  const subTab = subTabKeys.indexOf(subTabKey) >= 0 ? subTabKeys.indexOf(subTabKey) : 0;

  // Give Feedback state
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Received / Given feedback state
  const [receivedFeedback, setReceivedFeedback] = useState([]);
  const [givenFeedback, setGivenFeedback] = useState([]);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [givenLoading, setGivenLoading] = useState(false);

  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── load employees list ───────────────────────────────────────────────────
  useEffect(() => {
    setEmployeesLoading(true);
    fetch(API_ENDPOINTS.EMPLOYEES, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => setEmployees(data))
      .catch(() => setEmployees([]))
      .finally(() => setEmployeesLoading(false));
  }, []);

  // ── load received feedback ────────────────────────────────────────────────
  const loadReceivedFeedback = useCallback(() => {
    setReceivedLoading(true);
    fetch(API_ENDPOINTS.FEEDBACK_RECEIVED, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => setReceivedFeedback(Array.isArray(data) ? data : []))
      .catch(() => setReceivedFeedback([]))
      .finally(() => setReceivedLoading(false));
  }, []);

  // ── load given feedback ───────────────────────────────────────────────────
  const loadGivenFeedback = useCallback(() => {
    setGivenLoading(true);
    fetch(API_ENDPOINTS.FEEDBACK_GIVEN, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => setGivenFeedback(Array.isArray(data) ? data : []))
      .catch(() => setGivenFeedback([]))
      .finally(() => setGivenLoading(false));
  }, []);

  useEffect(() => {
    if (subTab === 1) loadReceivedFeedback();
    if (subTab === 2) loadGivenFeedback();
  }, [subTab, loadReceivedFeedback, loadGivenFeedback]);

  // ── submit handler ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedEmployee) {
      setSnackbar({ open: true, message: t('feedback_warn_select_employee'), severity: 'warning' });
      return;
    }
    if (!message.trim()) {
      setSnackbar({ open: true, message: t('feedback_warn_write_message'), severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.FEEDBACK_SUBMIT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_email: selectedEmployee.email,
          message: message.trim(),
          badge_id: selectedBadge || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: t('feedback_success_submitted'), severity: 'success' });
        setSelectedEmployee(null);
        setSelectedBadge('');
        setMessage('');
      } else {
        setSnackbar({ open: true, message: data.message || t('feedback_error_submit_failed'), severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: t('feedback_error_generic'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubTabChange = (_, newValue) => {
    setSearchParams(prev => {
      prev.set('sub', subTabKeys[newValue]);
      return prev;
    });
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('feedback_title')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          {t('feedback_subtitle') || 'Give and receive peer feedback to help your team grow.'}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          variant={isSmDown ? 'fullWidth' : 'standard'}
          sx={{ minHeight: 48 }}
        >
          <Tab
            icon={<SendIcon fontSize="small" />}
            iconPosition="start"
            label={t('feedback_tab_give')}
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
          <Tab
            icon={<InboxIcon fontSize="small" />}
            iconPosition="start"
            label={t('feedback_tab_received')}
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
          <Tab
            icon={<OutboxIcon fontSize="small" />}
            iconPosition="start"
            label={t('feedback_tab_given')}
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      {/* ── Give Feedback ──────────────────────────────────────────────────── */}
      {subTab === 0 && (
        <Box sx={{ maxWidth: 600 }}>
          <Typography variant="body2" sx={{ color: '#555', mb: 3 }}>
            {t('feedback_give_description')}
          </Typography>

          {/* Employee search */}
          <Autocomplete
            options={employees}
            getOptionLabel={(opt) => `${opt.fullname || ''} (${opt.email})`}
            value={selectedEmployee}
            onChange={(_, val) => setSelectedEmployee(val)}
            loading={employeesLoading}
            isOptionEqualToValue={(opt, val) => opt.email === val.email}
            renderOption={(props, option) => (
              <li {...props} key={option.email}>
                <ListItemText
                  primary={option.fullname || option.email}
                  secondary={option.email}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('feedback_search_employee')}
                placeholder={t('feedback_search_placeholder')}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {employeesLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Badge selector */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="badge-label">
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmojiEventsIcon fontSize="small" /> {t('feedback_badge_label')}
              </Box>
            </InputLabel>
            <Select
              labelId="badge-label"
              value={selectedBadge}
              label={t('feedback_badge_label')}
              onChange={(e) => setSelectedBadge(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('feedback_badge_none')}</em>
              </MenuItem>
              {FEEDBACK_BADGES.map((badge) => (
                <MenuItem key={badge.id} value={badge.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <BadgeIcon icon={badge.icon} size={64} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{t(`feedback_badge_${badge.id}_name`)}</Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>{t(`feedback_badge_${badge.id}_desc`)}</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Feedback message */}
          <TextField
            label={t('feedback_message_label')}
            placeholder={t('feedback_message_placeholder')}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${message.length} / 2000`}
            sx={{ mb: 3 }}
          />

          <LoadingButton
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            loading={submitting}
            loadingLabel={t('feedback_button_sending')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }
            }}
          >
            {t('feedback_button_send')}
          </LoadingButton>
        </Box>
      )}

      {/* ── Received Feedback ──────────────────────────────────────────────── */}
      {subTab === 1 && (
        <Box>
          {receivedLoading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading feedback...
              </Typography>
            </Box>
          ) : receivedFeedback.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('feedback_received_empty')}
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                {receivedFeedback.length === 1 ? t('feedback_received_one') : `${receivedFeedback.length} ${t('feedback_received_many')}`}
              </Typography>
              {receivedFeedback.map((entry, idx) => (
                <FeedbackCard key={idx} entry={entry} direction="received" />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Given Feedback ─────────────────────────────────────────────────── */}
      {subTab === 2 && (
        <Box>
          {givenLoading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading feedback...
              </Typography>
            </Box>
          ) : givenFeedback.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('feedback_given_empty')}
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                {givenFeedback.length === 1 ? t('feedback_given_one') : `${givenFeedback.length} ${t('feedback_given_many')}`}
              </Typography>
              {givenFeedback.map((entry, idx) => (
                <FeedbackCard key={idx} entry={entry} direction="given" />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ mt: 4 }} />

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
